import { config } from "../config";
import { getBaseHeaders } from "./utils";
import { clearUserId } from "./validation";
import { toast } from "sonner";
import { isNative } from "@/utils/platform";
import { nativeTokenStore } from "./nativeTokenStore";

const clearNativeGoogleSession = async () => {
  if (!isNative()) return;
  try {
    const { SocialLogin } = await import("@capgo/capacitor-social-login");
    await SocialLogin.logout({ provider: "google" });
  } catch {
    /* ignore — user may not have signed in via Google */
  }
};

const clearAllCookies = () => {
  const cookiesToClear = ['access_token', 'refresh_token', 'session_token', 'user_id'];
  const domains = window.location.hostname.includes('localhost')
    ? ['localhost']
    : ['.buyfors.com', 'buyfors.com', 'www.buyfors.com', 'api.buyfors.com'];
  const paths = ['/', '/api'];

  cookiesToClear.forEach(cookieName => {
    domains.forEach(domain => {
      paths.forEach(path => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
      });
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    });
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });

  localStorage.removeItem('buyfors_refresh_lock');
  localStorage.removeItem('buyfors_refresh_result');

  clearUserId();
};

export const clearAllClientAuthState = async () => {
  if (isNative()) {
    try { await nativeTokenStore.clear(); } catch { /* ignore */ }
    await clearNativeGoogleSession();
  }
  clearAllCookies();
  localStorage.removeItem('publicWishlistToken');
  sessionStorage.removeItem('ssoProcessed');
  sessionStorage.removeItem('ssoInProgress');
  sessionStorage.removeItem('lastValidation');
  sessionStorage.removeItem('loginReturnUrl');
};

export const logout = async () => {
  try {
    const native = isNative();
    const headers: Record<string, string> = { ...getBaseHeaders() };

    if (native) {
      const access = await nativeTokenStore.getAccessToken();
      const refresh = await nativeTokenStore.getRefreshToken();
      if (access) headers['Authorization'] = `Bearer ${access}`;
      if (refresh) headers['X-Refresh-Token'] = refresh;
    }

    const fetchInit: RequestInit = {
      method: 'POST',
      headers,
    };
    if (!native) {
      fetchInit.credentials = 'include';
    }

    const response = await fetch(`${config.API_URL}/user/logout`, fetchInit);

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.msg || data.detail || data.message || data.error || response.statusText;
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    if (native) {
      await nativeTokenStore.clear();
      await clearNativeGoogleSession();
    }
    clearAllCookies();
    localStorage.removeItem('publicWishlistToken');
    sessionStorage.removeItem('ssoProcessed');
    sessionStorage.removeItem('lastValidation');

    const successMessage = data.msg || data.detail || data.message || 'Logged out successfully';
    toast.success(successMessage);

    window.location.href = '/login';

    return true;
  } catch (error) {
    if (isNative()) {
      try { await nativeTokenStore.clear(); } catch { /* ignore */ }
      await clearNativeGoogleSession();
    }
    clearAllCookies();
    if (error instanceof Error) {
      toast.error(error.message);
    } else {
      toast.error('An unexpected error occurred during logout');
    }
    throw error;
  }
};
